import { type HTMLAttributes } from 'react'
import { motion } from 'motion/react'
import { Heart } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { useToggleFavorite } from '../lib/use-toggle-favorite'
import type { ProductType } from '../api/use-favorites'

export type FavoriteButtonSize = 'sm' | 'md' | 'lg'
export type FavoriteButtonVariant = 'default' | 'card' | 'floating' | 'minimal'

export interface FavoriteButtonProps extends Omit<HTMLAttributes<HTMLButtonElement>, 'onClick' | 'onToggle'> {
  /** ID товара */
  productId: string
  /** Тип товара */
  productType?: ProductType
  /** Размер кнопки */
  size?: FavoriteButtonSize
  /** Вариант отображения */
  variant?: FavoriteButtonVariant
  /** Показывать анимацию */
  animated?: boolean
  /** Callback после изменения */
  onToggle?: (isFavorite: boolean) => void
  /** Callback для haptic feedback */
  onHapticFeedback?: (type: 'light' | 'medium' | 'heavy') => void
}

/**
 * Кнопка избранного
 */
export function FavoriteButton({
  productId,
  productType = 'tea',
  size = 'md',
  variant = 'default',
  animated = true,
  onToggle,
  onHapticFeedback,
  className,
  ...props
}: FavoriteButtonProps) {
  const { isFavorite, isLoading, toggle } = useToggleFavorite(productId, productType)
  
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    
    if (isLoading) return
    
    toggle()
    onToggle?.(!isFavorite)
    onHapticFeedback?.('light')
  }
  
  const iconSizes: Record<FavoriteButtonSize, string> = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
  }
  
  const buttonSizes: Record<FavoriteButtonSize, string> = {
    sm: 'p-1.5',
    md: 'p-2',
    lg: 'p-3',
  }
  
  const variantStyles: Record<FavoriteButtonVariant, string> = {
    default: cn(
      'rounded-full transition-colors',
      isFavorite
        ? 'bg-primary/10 dark:bg-primary-dark/20 text-primary dark:text-primary-light'
        : 'bg-gray-100 dark:bg-dark-elevated text-gray-400 dark:text-gray-500 hover:text-primary dark:hover:text-primary-light hover:bg-gray-200 dark:hover:bg-dark-elevated/80'
    ),
    card: cn(
      'rounded-full backdrop-blur-sm transition-all duration-150',
      'bg-white/90 dark:bg-dark-elevated/90',
      isFavorite
        ? 'text-primary dark:text-primary-light'
        : 'text-gray-400 dark:text-gray-500 hover:text-primary dark:hover:text-primary-light'
    ),
    floating: cn(
      'rounded-full shadow-lg transition-all duration-200',
      isFavorite
        ? 'bg-primary dark:bg-primary-dark text-white shadow-primary/25 dark:shadow-primary-dark/25'
        : 'bg-white dark:bg-dark-elevated text-gray-400 dark:text-gray-500 hover:text-primary dark:hover:text-primary-light hover:shadow-xl'
    ),
    minimal: cn(
      'rounded-full transition-colors',
      isFavorite
        ? 'text-primary dark:text-primary-light'
        : 'text-gray-400 dark:text-gray-500 hover:text-primary dark:hover:text-primary-light'
    ),
  }
  
  const buttonContent = (
    <Heart
      className={cn(
        iconSizes[size],
        'flex-shrink-0 transition-transform',
        isFavorite && 'fill-current'
      )}
    />
  )
  
  if (animated) {
    return (
      <motion.button
        type="button"
        onClick={handleClick}
        disabled={isLoading}
        whileTap={{ scale: 0.9 }}
        animate={isFavorite ? { scale: [0.9, 1.15, 1] } : undefined}
        transition={{ duration: 0.3 }}
        className={cn(
          buttonSizes[size],
          variantStyles[variant],
          'touch-target flex items-center justify-center',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          className
        )}
        aria-label={isFavorite ? 'Удалить из избранного' : 'Добавить в избранное'}
        aria-pressed={isFavorite}
        {...(props as React.ComponentProps<typeof motion.button>)}
      >
        {buttonContent}
      </motion.button>
    )
  }
  
  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isLoading}
      className={cn(
        buttonSizes[size],
        variantStyles[variant],
        'touch-target flex items-center justify-center',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        className
      )}
      aria-label={isFavorite ? 'Удалить из избранного' : 'Добавить в избранное'}
      aria-pressed={isFavorite}
      {...props}
    >
      {buttonContent}
    </button>
  )
}

/**
 * Индикатор избранного (без интерактивности)
 */
export function FavoriteIndicator({ 
  productId, 
  productType = 'tea',
  size = 'md',
  className 
}: { 
  productId: string
  productType?: ProductType
  size?: FavoriteButtonSize
  className?: string 
}) {
  const { isFavorite } = useToggleFavorite(productId, productType)
  
  if (!isFavorite) return null
  
  const iconSizes: Record<FavoriteButtonSize, string> = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5',
  }
  
  return (
    <div 
      className={cn(
        'flex items-center justify-center text-primary dark:text-primary-light',
        className
      )}
      aria-label="В избранном"
    >
      <Heart className={cn(iconSizes[size], 'fill-current')} />
    </div>
  )
}
